import { md5 } from "js-md5";

import { arrayBufferToSha256Hex } from "utils/misc";

import { procoreClient } from "./client";

export type CreateUploadParams = {
    companyId: number;
    projectId: number;
    name: string;
    contentType: string;
    size: number;
    sha256: string;
    md5: string;
};

export type CreateUploadResponse = {
    id: string;
    url: string;
    url_created_at: string;
    url_expires_at: string;
    headers: {
        "x-amz-acl": string;
        "x-amz-checksum-sha256": string;
        "Content-Disposition": string;
        "Content-Length": string;
        "Content-Type": string;
        "Content-MD5": string;
    };
};

/**
 * Create an upload (FAS) in Procore.
 * POST /rest/v2.0/companies/:company_id/projects/:project_id/uploads
 */
export async function createUpload(params: CreateUploadParams): Promise<CreateUploadResponse> {
    const response = await procoreClient<{ data: CreateUploadResponse }>(
        `/rest/v2.0/companies/${params.companyId}/projects/${params.projectId}/uploads`,
        {
            method: "POST",
            body: {
                name: params.name,
                content_type: params.contentType,
                size: params.size,
                sha256: params.sha256,
                md5: params.md5,
            },
        },
    );

    return response.data;
}

export type UploadToFasParams = {
    companyId: number;
    projectId: number;
    fileName: string;
    contentType: string;
    file: ArrayBuffer;
};

/**
 * Compute MD5 and SHA256 of the file, create an upload via Procore FAS, then PUT the file to the returned URL.
 */
export async function uploadToFas(params: UploadToFasParams): Promise<CreateUploadResponse> {
    const { companyId, projectId, fileName, contentType, file } = params;
    const size = file.byteLength;

    const md5Hex = md5(new Uint8Array(file));
    const sha256Hex = await arrayBufferToSha256Hex(file);

    const upload = await createUpload({
        companyId,
        projectId,
        name: fileName,
        contentType,
        size,
        sha256: sha256Hex,
        md5: md5Hex,
    });

    const headers = upload.headers as Record<string, string>;
    const putResponse = await fetch(upload.url, {
        method: "PUT",
        headers,
        body: file,
    });

    if (!putResponse.ok) {
        throw new Error(`FAS upload failed: ${putResponse.status} ${putResponse.statusText}`);
    }

    return upload;
}
